import React, { useEffect, useState, useRef } from 'react';
import { Clock, AlertCircle, SmartphoneIcon, QrCodeIcon, CheckCircle2Icon, ShieldCheckIcon, ChevronDownIcon, ChevronUpIcon } from 'lucide-react';
import { formatPrice } from '../../utils/formatters';
interface RaastQRPaymentProps {
  totalAmount: number;
  onComplete: () => void;
  onCancel: () => void;
}
export function RaastQRPayment({
  totalAmount,
  onComplete,
  onCancel
}: RaastQRPaymentProps) {
  // 5 minutes in seconds
  const [timeLeft, setTimeLeft] = useState(300);
  const [isExpired, setIsExpired] = useState(false);
  const [showGuide, setShowGuide] = useState(false);
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;
  // Countdown timer for QR validity (5 mins)
  useEffect(() => {
    if (timeLeft <= 0) {
      setIsExpired(true);
      return;
    }
    const timerId = setInterval(() => {
      setTimeLeft(prev => prev - 1);
    }, 1000);
    return () => clearInterval(timerId);
  }, [timeLeft]);
  // Auto-redirect timer (50 seconds) to simulate successful payment
  useEffect(() => {
    const successTimerId = setTimeout(() => {
      onCompleteRef.current();
    }, 50000);
    return () => clearTimeout(successTimerId);
  }, []);
  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };
  return <div className="flex flex-col items-center justify-center py-6 text-center" data-id="element-608" data-mp="10yw49pm4d2mw">
      <div className="mb-6" data-id="element-609" data-mp="vxqbxswdfhdw">
        <img src="https://cdn.magicpatterns.com/uploads/oZFx4MBZmzvJA34NArst7p/RAAST_Logo.png" alt="RAAST" className="h-12 object-contain mx-auto mb-2" data-id="element-610" data-mp="w0m9vh87y62c" />
        <h2 className="text-xl font-bold text-gray-900" data-id="element-611" data-mp="myv4yeli639j">
          Scan to Pay {formatPrice(totalAmount)}
        </h2>
        <p className="text-sm text-gray-500 mt-1" data-id="element-612" data-mp="2mafexqigemxc">
          Open your banking app and scan the QR code below
        </p>
      </div>

      {isExpired ? <div className="bg-red-50 border border-red-200 rounded-xl p-8 max-w-sm w-full flex flex-col items-center" data-id="element-613" data-mp="30ghbyhfi34h5">
          <AlertCircle className="h-12 w-12 text-red-500 mb-4" data-id="element-614" data-mp="357pqfrvotg16" />
          <h3 className="text-lg font-bold text-red-900 mb-2" data-id="element-615" data-mp="3590t1odechqp">
            QR Code Expired
          </h3>
          <p className="text-sm text-red-700 mb-6 text-center" data-id="element-616" data-mp="2wevu0ho40003">
            This payment session has expired. Please generate a new QR code to
            continue.
          </p>
          <button onClick={onCancel} className="w-full py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 transition-colors" data-id="element-617" data-mp="3k2cckslkogdu">
            Return to Checkout
          </button>
        </div> : <div className="flex flex-col items-center" data-id="element-618" data-mp="3f4km830kt2br">
          <div className="bg-white p-4 rounded-xl shadow-sm border-2 border-gray-100 mb-6 relative" data-id="element-619" data-mp="3f7gjdp4lnmz5">
            {/* Generic QR Code placeholder */}
            <img src="https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=RAAST_PAYMENT_DEMO" alt="Payment QR Code" className="w-48 h-48" data-id="element-620" data-mp="23qgrgum5za5n" />

            {/* Scanning animation overlay */}
            <div className="absolute inset-0 bg-gradient-to-b from-transparent via-blue-400/20 to-transparent h-full w-full animate-[scan_2s_ease-in-out_infinite]" style={{
          backgroundSize: '100% 20%'
        }} data-id="element-621" data-mp="1r8e3t6qjrgfd" />
          </div>

          <div className="flex items-center justify-center space-x-2 text-sm font-medium text-gray-700 bg-gray-100 px-4 py-2 rounded-full mb-6" data-id="element-622" data-mp="2092h1z849cr7">
            <Clock className="h-4 w-4 text-blue-600" data-id="element-623" data-mp="18hmz4ihi8e7b" />
            <span data-id="element-624" data-mp="18ixze8bqr3to">
              QR expires in:{' '}
              <span className="text-blue-600 font-bold" data-id="element-625" data-mp="1m8rpsv2w9w5i">
                {formatTime(timeLeft)}
              </span>
            </span>
          </div>

          {/* User Guidelines */}
          <div className="w-full max-w-sm bg-gray-50 border border-gray-200 rounded-xl mb-6 overflow-hidden text-left" data-id="element-626" data-mp="trp5vo4pwyb9">
            <button onClick={() => setShowGuide(!showGuide)} className="w-full flex items-center justify-between px-4 py-3 text-sm font-semibold text-gray-700 hover:bg-gray-100 transition-colors" data-id="element-627" data-mp="os2tfgwprm3i">
              <span className="flex items-center gap-2" data-id="element-628" data-mp="17fzah09x8ozo">
                <SmartphoneIcon className="w-4 h-4 text-blue-600" data-id="element-629" data-mp="1ce0t5k3fqmam" />
                How to pay using QR code?
              </span>
              {showGuide ? <ChevronUpIcon className="w-4 h-4 text-gray-500" data-id="element-630" data-mp="1h5skhwuf0w1t" /> : <ChevronDownIcon className="w-4 h-4 text-gray-500" data-id="element-631" data-mp="1h6k3872wyzao" />}
            </button>
            {showGuide && <div className="px-4 pb-4 pt-1 border-t border-gray-200" data-id="element-632" data-mp="1q5fc5qxcenho">
                <div className="space-y-4" data-id="element-633" data-mp="1q3ks0etwazen">
                  <div className="flex items-start gap-3" data-id="element-634" data-mp="1v3z0fxzesbd7">
                    <div className="w-7 h-7 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-635" data-mp="1v2fi81u6zq8s">
                      <span className="text-xs font-bold text-blue-700" data-id="element-636" data-mp="qhci9v3dchn">1</span>
                    </div>
                    <div data-id="element-637" data-mp="5pamvtf30ndl">
                      <p className="text-sm font-medium text-gray-800" data-id="element-638" data-mp="5qlrtwk9k16a">
                        Open your banking app
                      </p>
                      <p className="text-xs text-gray-500 mt-0.5" data-id="element-639" data-mp="2j1y91wrdlmw9">
                        Launch your bank's mobile app (e.g. HBL Mobile,
                        JazzCash, Easypaisa, Meezan, etc.)
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3" data-id="element-640" data-mp="25a0a2erdp7dt">
                    <div className="w-7 h-7 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-641" data-mp="31z7xjsbk5qkd">
                      <span className="text-xs font-bold text-blue-700" data-id="element-642" data-mp="2xy5sguuymkah">2</span>
                    </div>
                    <div data-id="element-643" data-mp="2swq8750lwht5">
                      <p className="text-sm font-medium text-gray-800 flex items-center gap-1" data-id="element-644" data-mp="2t04dal0nn54a">
                        Navigate to QR Pay / Scan & Pay
                        <QrCodeIcon className="w-3.5 h-3.5 text-blue-500" data-id="element-645" data-mp="1hj4k7n6hgm97" />
                      </p>
                      <p className="text-xs text-gray-500 mt-0.5" data-id="element-646" data-mp="1cji5f9b0axya">
                        Look for "QR Pay", "Scan & Pay", or "RAAST QR" option in
                        your banking app.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3" data-id="element-647" data-mp="1508xlki5f7bc">
                    <div className="w-7 h-7 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-648" data-mp="14zqsrzhf7krt">
                      <span className="text-xs font-bold text-blue-700" data-id="element-649" data-mp="1a16ezuvc3xbs">3</span>
                    </div>
                    <div data-id="element-650" data-mp="v8mihd2owo3b">
                      <p className="text-sm font-medium text-gray-800 flex items-center gap-1" data-id="element-651" data-mp="101pdzqr127m5">
                        Scan the QR code
                        <CheckCircle2Icon className="w-3.5 h-3.5 text-green-500" data-id="element-652" data-mp="mbc61oe4yswt" />
                      </p>
                      <p className="text-xs text-gray-500 mt-0.5" data-id="element-653" data-mp="qbm7cnqzgv5c">
                        Point your phone camera at the QR code above. Verify the
                        merchant (Gadgets Zone) and amount (
                        {formatPrice(totalAmount)}).
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3" data-id="element-654" data-mp="3ovfiknu4n4iu">
                    <div className="w-7 h-7 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-655" data-mp="3ox0eohw3avit">
                      <ShieldCheckIcon className="w-4 h-4 text-green-600" data-id="element-656" data-mp="2nz506y14ghy" />
                    </div>
                    <div data-id="element-657" data-mp="gnqr6uyp9371">
                      <p className="text-sm font-medium text-gray-800" data-id="element-658" data-mp="gmytneljkve4">
                        Confirm & authenticate
                      </p>
                      <p className="text-xs text-gray-500 mt-0.5" data-id="element-659" data-mp="bodulii10hr9">
                        Tap "Pay" and authenticate with your PIN or biometrics.
                        This page will update automatically once payment is
                        confirmed.
                      </p>
                    </div>
                  </div>
                </div>
              </div>}
          </div>

          <p className="text-xs text-gray-500 animate-pulse" data-id="element-660" data-mp="ve7s8d7f9r3e">
            Waiting for payment confirmation...
          </p>

          <button onClick={onCancel} className="mt-6 text-sm text-gray-500 hover:text-gray-700 underline" data-id="element-661" data-mp="21tyqlfm4mg51">
            Cancel Payment
          </button>
        </div>}
    </div>;
}